import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Categoria, type Movimento, type RispostaMovimenti } from '../api';
import {
  Caricamento,
  Dialogo,
  Errore,
  Etichetta,
  Importo,
  LinkFattura,
  Scheda,
  Vuoto,
  Paginazione,
  useAlberoCategorie,
  useAvvisi,
  useCarico,
  useDebounce,
  useFiltriUrl,
} from '../componenti/base';
import { Campo } from '../componenti/base';
import { ripartisciSuDocumenti } from '@mastro/core/abbinamenti';
import { formattaData, formattaEuro, plurale } from '../formato';
import { useSessione } from '../main';
import { DialogoRegola } from './regole';

interface AbbinamentoSospetto {
  movimentoId: string;
  documentoId: string;
  quotaCent: number;
  numero: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  scadenza: string;
  dataMovimento: string;
  dicitura: string;
  controparte: string | null;
  scartoGiorni: number;
  gravita: 'ALTA' | 'MEDIA' | 'BASSA';
  motivo: string;
}

interface PropostaAbbinamento {
  movimentoId: string;
  documentoId: string;
  importoCent: number;
  giorniDiScarto: number;
  motivo: string;
  numeroInCausale: boolean;
  movimento: { data: string; importoCent: number; dicitura: string; categoriaId: string | null };
  documento: { numero: string; tipo: 'ATTIVA' | 'PASSIVA'; controparte: string | null; categoriaId: string | null };
}

interface Candidato {
  id: string;
  numero: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  dataCompetenza: string;
  dataCassaAttesa: string;
  totaleCent: number;
  residuoCent: number;
  controparte: string | null;
}

interface F24Aperto {
  id: string;
  descrizione: string;
  dataScadenza: string;
  saldoCent: number;
}

interface Giroconto {
  uscita_id: string;
  entrata_id: string;
  importo_cent: number;
  data_uscita: string;
  data_entrata: string;
  conto_uscita: string;
  conto_entrata: string;
  dicitura_uscita: string;
  dicitura_entrata: string;
}

export function Riconciliazione() {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  /**
   * Filtri e pagina **nell'indirizzo**. Da qui si entra di continuo in una
   * fattura o in un F24, e tornare indietro perdendo la ricerca e la pagina
   * significa rifare il giro per ogni movimento che si lavora.
   *
   * `soloDaFare` parte acceso: l'elenco serve a lavorare, non a consultare.
   * Nell'indirizzo compare solo quando lo si spegne.
   */
  const { filtri, imposta, azzera } = useFiltriUrl({ cerca: '', da: '', a: '', tutti: '', pagina: '' });
  const cerca = filtri.cerca;
  const da = filtri.da;
  const a = filtri.a;
  const soloDaFare = filtri.tutti !== 'si';
  const scarto = Number(filtri.pagina) || 0;
  // Cambiare un filtro riporta alla prima pagina: restare alla quarta di un
  // elenco appena ristretto vuol dire quasi sempre guardare il vuoto.
  const setScarto = (v: number) => imposta({ pagina: v > 0 ? String(v) : '' });
  const [scelto, setScelto] = useState<Movimento | null>(null);
  const [categorizza, setCategorizza] = useState<Movimento | null>(null);
  const [associaF24, setAssociaF24] = useState<Movimento | null>(null);
  /**
   * Convalidare un movimento privo di voce richiede prima l'assegnazione della
   * voce. Non è una formalità: senza, il movimento uscirebbe dall'elenco di
   * lavorazione lasciando il proprio importo in «Da identificare», dove non
   * verrebbe più intercettato da nessuna verifica.
   */
  const [convalidaDopoVoce, setConvalidaDopoVoce] = useState<string | null>(null);

  const cercaRitardata = useDebounce(cerca);

  // Cento per pagina, non trecento: trecento righe non si scorrono, si
  // abbandonano — e la paginazione esiste apposta per non doverle caricare
  // tutte insieme.
  const query = new URLSearchParams({ limite: '100', scarto: String(scarto) });
  if (soloDaFare) query.set('daRiconciliare', 'true');
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (da) query.set('da', da);
  if (a) query.set('a', a);

  const movimenti = useCarico(
    () => api.get<RispostaMovimenti>(`/api/movimenti?${query}`),
    [soloDaFare, cercaRitardata, da, a, scarto],
  );
  const elenco = movimenti.dati?.movimenti ?? [];
  const categorie = useCarico(() => api.get<Categoria[]>('/api/categorie'), []);
  const giroconti = useCarico(() => api.get<Giroconto[]>('/api/riconciliazione/giroconti'), []);
  const proposte = useCarico(
    () => api.get<{ proposte: PropostaAbbinamento[]; totale: number }>('/api/riconciliazione/proposte-abbinamento'),
    [],
  );
  const [confermando, setConfermando] = useState<string | null>(null);
  /**
   * Voce scelta riga per riga, prima di confermare. Il valore di partenza è
   * quello che il sistema già sa — la proposta di una regola sul movimento,
   * altrimenti la voce della fattura — così nel caso normale non c'è niente da
   * toccare, e quando invece è sbagliata si corregge **prima** di confermare,
   * invece di dover tornare sul movimento dopo.
   */
  const { piatto } = useAlberoCategorie(categorie.dati ?? []);
  const [voci, setVoci] = useState<Record<string, string>>({});
  const voceDi = (p: PropostaAbbinamento) =>
    voci[p.movimentoId] ?? p.movimento.categoriaId ?? p.documento.categoriaId ?? '';

  const [inBlocco, setInBlocco] = useState(false);
  const sospetti = useCarico(() => api.get<AbbinamentoSospetto[]>('/api/riconciliazione/abbinamenti-sospetti'), []);

  /**
   * «Lo so che sembra strano, è giusto così.»
   *
   * Un pagamento lontano dalla scadenza non è per forza un errore: capita di
   * pagare con mesi di ritardo perché i soldi non c'erano. Senza un modo di
   * dirlo, la segnalazione tornerebbe per sempre — e un avviso che si
   * ripresenta dopo essere stato valutato smette di essere letto, portandosi
   * dietro anche quelli veri. Non chiede conferma: non cambia niente, e se
   * capita per sbaglio basta ricaricare per rivederlo.
   */
  const validaSospetto = async (a: AbbinamentoSospetto) => {
    try {
      await api.post('/api/riconciliazione/valida', { movimentoId: a.movimentoId, documentoId: a.documentoId });
      avvisa(`Abbinamento confermato: la fattura ${a.numero} non verrà più segnalata.`);
      sospetti.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non è stato possibile validare.', 'errore');
    }
  };

  /**
   * La convalida dichiara conclusa la riconciliazione di un movimento privo di
   * documento: commissioni bancarie, F24, stipendi. Senza, resterebbero in
   * lavorazione a tempo indeterminato, rendendo irreperibili le poche decine
   * che una fattura la attendono davvero.
   *
   * Diverso da «Escludi»: l'importo è transitato e continua a incidere sulla
   * disponibilità di cassa.
   */
  const registraConvalida = async (id: string, valore: boolean) => {
    try {
      await api.post('/api/movimenti/senza-documento', { movimentiId: [id], valore });
      avvisa(
        valore
          ? 'Movimento convalidato: la riconciliazione è conclusa e l’importo resta nel calcolo di cassa.'
          : 'Convalida annullata: il movimento torna fra quelli da riconciliare.',
      );
      movimenti.ricarica();
      proposte.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Registrazione non riuscita.', 'errore');
    }
  };

  const convalida = (m: Movimento, valore: boolean) => {
    if (valore && !m.categoriaId) {
      setConvalidaDopoVoce(m.id);
      setCategorizza(m);
      return;
    }
    void registraConvalida(m.id, valore);
  };

  const disabbinaSospetto = async (a: AbbinamentoSospetto) => {
    if (!confirm(`Disabbinare il pagamento del ${formattaData(a.dataMovimento, organizzazione.formatoData)} dalla fattura ${a.numero}?\n\nIl movimento resta in banca e la fattura torna scoperta.`))
      return;
    try {
      await api.del('/api/riconciliazione/abbina', { movimentoId: a.movimentoId, documentoId: a.documentoId });
      avvisa('Abbinamento rimosso.');
      sospetti.ricarica();
      proposte.ricarica();
      movimenti.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Non è stato possibile disabbinare.', 'errore');
    }
  };
  const solide = (proposte.dati?.proposte ?? []).filter((p) => p.giorniDiScarto === 0 || p.numeroInCausale);

  /**
   * Conferma in blocco le proposte a scarto zero. Il server le **ricalcola**
   * prima di scriverle, e rifiuta se nel frattempo sono cambiate: confermare
   * alla cieca un elenco vecchio è il modo di scrivere abbinamenti che non
   * stanno più in piedi.
   */
  const confermaTutte = async () => {
    if (
      !confirm(
        `Confermare ${solide.length} abbinamenti in un colpo solo?\n\n` +
          'Sono quelli in cui la causale cita il numero della fattura, oppure importo, scadenza e nome ' +
          'coincidono nello stesso giorno. ' +
          'Ognuno si può disabbinare singolarmente dalla pagina della fattura.',
      )
    )
      return;
    setInBlocco(true);
    try {
      const r = await api.post<{ confermate: number; saltate: number }>(
        '/api/riconciliazione/proposte-abbinamento/conferma',
        { scartoMassimo: 0, attese: solide.length },
      );
      avvisa(
        `${plurale(r.confermate, 'abbinamento confermato', 'abbinamenti confermati')}` +
          (r.saltate ? `, ${r.saltate} saltati perché nel frattempo non reggevano più.` : '.'),
      );
      proposte.ricarica();
      movimenti.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Conferma in blocco non riuscita.', 'errore');
    } finally {
      setInBlocco(false);
    }
  };

  const confermaProposta = async (p: PropostaAbbinamento) => {
    setConfermando(p.movimentoId);
    try {
      await api.post('/api/riconciliazione/abbina', {
        movimentoId: p.movimentoId,
        documentoId: p.documentoId,
        categoriaId: voceDi(p) || null,
      });
      avvisa(`Abbinato alla fattura ${p.documento.numero}.`);
      proposte.ricarica();
      movimenti.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Abbinamento non riuscito.', 'errore');
    } finally {
      setConfermando(null);
    }
  };
  const regole = useCarico(() => api.get<{ id: string }[]>('/api/regole'), []);

  const escludi = async (ids: string[], escluso: boolean) => {
    try {
      await api.post('/api/movimenti/escludi', { movimentiId: ids, escluso });
      avvisa(escluso ? 'Movimenti esclusi dai totali. Restano visibili.' : 'Movimenti reinclusi.');
      movimenti.ricarica();
      giroconti.ricarica();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Operazione non riuscita.', 'errore');
    }
  };

  return (
    <div className="pagina space-y-5">
      <header>
        <h1 className="text-lg font-semibold tracking-tight">Riconciliazione</h1>
        <p className="text-sm text-inchiostro-500">
          Collegare un movimento a una fattura fa calare il residuo della fattura, e con lui il suo peso sul
          previsionale. Un bonifico può coprire più fatture e una fattura può essere pagata da più movimenti.
        </p>
      </header>

      {(sospetti.dati ?? []).length > 0 && (
        <Scheda
          titolo="Abbinamenti da controllare"
          sottotitolo="Pagamenti lontani dalla scadenza della fattura. A volte è un errore — due fatture con lo stesso numero in anni diversi — ma spesso è solo un pagamento tardivo, che è una cosa normale. Se l’abbinamento è giusto, «Valida» lo toglie dall’elenco senza cambiarlo."
          senzaPadding
        >
          <table className="dati">
            <thead>
              <tr>
                <th>Fattura</th>
                <th>Controparte</th>
                <th>Scadenza</th>
                <th>Pagato il</th>
                <th className="text-right">Quota</th>
                <th>Causale del bonifico</th>
                <th>Perché</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sospetti.dati!.map((a) => (
                <tr key={`${a.movimentoId}-${a.documentoId}`}>
                  <td>
                    <LinkFattura id={a.documentoId} className="underline underline-offset-2">
                      {a.numero}
                    </LinkFattura>
                    {a.gravita === 'ALTA' && (
                      <span className="ml-2">
                        <Etichetta testo="quasi certamente sbagliato" tono="attenzione" />
                      </span>
                    )}
                  </td>
                  <td className="max-w-56 truncate">{a.controparte}</td>
                  <td className="num whitespace-nowrap">{formattaData(a.scadenza, organizzazione.formatoData)}</td>
                  <td className="num whitespace-nowrap">{formattaData(a.dataMovimento, organizzazione.formatoData)}</td>
                  <td className="text-right"><Importo cent={a.quotaCent} /></td>
                  {/* La causale è la prova: «fatture di gennaio - febbraio -
                      marzo» dice da sola che il ritardo è vero. Senza, la riga
                      chiedeva di giudicare un abbinamento senza mostrarlo. */}
                  <td className="max-w-80 text-xs" title={a.dicitura}>
                    <span className="line-clamp-2">{a.dicitura}</span>
                  </td>
                  <td className="text-xs text-inchiostro-500">{a.motivo.replace(/\*\*/g, '')}</td>
                  <td className="text-right">
                    {/* «Valida» prima di «Disabbina»: il caso più frequente è
                        che l'abbinamento sia giusto e il ritardo reale, non che
                        sia un errore da correggere. */}
                    <div className="flex justify-end gap-1">
                      <button
                        className="bottone-secondario px-2 py-1 text-xs"
                        title="L’abbinamento è corretto: non segnalarlo più"
                        onClick={() => validaSospetto(a)}
                      >
                        Valida
                      </button>
                      <button className="bottone-secondario px-2 py-1 text-xs" onClick={() => disabbinaSospetto(a)}>
                        Disabbina
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scheda>
      )}

      <PagamentiRitrovati
        percorso="/api/riconciliazione/proposte-gestionale"
        titolo="Pagate secondo Fatture in Cloud"
        sottotitolo="Fatture che il gestionale segna come pagate, con il movimento in banca che le salda: importo esatto — anche la somma di più fatture pagate insieme — e data vicina a quella di pagamento. Con il numero della fattura nella causale si accetta qualche centesimo di differenza. Finché non confermi, non è successo niente."
        spiegazioneConferma="Sono fatture che Fatture in Cloud dà per pagate, e per ognuna c’è in banca un movimento dell’importo esatto vicino alla data di pagamento."
        onConfermate={() => {
          proposte.ricarica();
          movimenti.ricarica();
        }}
      />
      <PagamentiRitrovati
        percorso="/api/riconciliazione/proposte-cumulative"
        titolo="Bonifici che saldano più fatture"
        sottotitolo="Per le fatture che non passano da Fatture in Cloud: un bonifico con il nome della controparte nella causale, il cui importo è esattamente la somma di alcune sue fatture aperte — e nessun'altra combinazione dà la stessa cifra. È la ricerca più esposta al caso: guardale una per una prima di confermarle."
        spiegazioneConferma="Per ognuna, l'importo del bonifico è esattamente la somma di quelle fatture della stessa controparte, e nessun'altra combinazione lo dà. Il gestionale però non lo conferma: guardale prima."
        onConfermate={() => {
          proposte.ricarica();
          movimenti.ricarica();
        }}
      />

      {(proposte.dati?.proposte ?? []).length > 0 && (
        <Scheda
          titolo="Abbinamenti proposti"
          sottotitolo="Stesso importo, stessa scadenza e il nome della controparte nella causale. Mastro li propone soltanto: finché non confermi, non è successo niente."
          senzaPadding
          azioni={
            <div className="flex items-center gap-3">
              <span className="text-sm text-inchiostro-500">{plurale(proposte.dati!.totale, 'proposta', 'proposte')}</span>
              {solide.length > 0 && (
                <button
                  className="bottone-primario"
                  disabled={inBlocco}
                  title="Quelle in cui la causale cita il numero della fattura, o in cui importo, scadenza e nome coincidono nello stesso giorno. Ognuna si può disabbinare."
                  onClick={confermaTutte}
                >
                  {inBlocco ? 'Confermo…' : `Conferma le ${solide.length} più sicure`}
                </button>
              )}
            </div>
          }
        >
          <div className="max-h-[28rem] overflow-y-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Movimento</th>
                  <th>Causale</th>
                  <th className="text-right">Importo</th>
                  <th>Fattura</th>
                  <th>Voce</th>
                  <th>Perché</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {proposte.dati!.proposte.map((p) => (
                  <tr key={p.movimentoId}>
                    <td className="num whitespace-nowrap">{formattaData(p.movimento.data, organizzazione.formatoData)}</td>
                    <td className="max-w-80 truncate" title={p.movimento.dicitura}>
                      {p.movimento.dicitura}
                    </td>
                    <td className="text-right">
                      <Importo cent={p.movimento.importoCent} tono="auto" />
                    </td>
                    <td className="whitespace-nowrap">
                      <LinkFattura id={p.documentoId} className="underline underline-offset-2">
                        {p.documento.numero}
                      </LinkFattura>
                      <span className="ml-2 text-xs text-inchiostro-500">{p.documento.controparte}</span>
                    </td>
                    <td>
                      <select
                        className="campo w-48 py-1 text-xs"
                        value={voceDi(p)}
                        onChange={(e) => setVoci((v) => ({ ...v, [p.movimentoId]: e.target.value }))}
                      >
                        <option value="">Nessuna voce</option>
                        {piatto
                          .filter(
                            ({ categoria }) =>
                              !categoria.sistema &&
                              categoria.verso === (p.movimento.importoCent > 0 ? 'ENTRATA' : 'USCITA'),
                          )
                          .map(({ categoria, livello }) => (
                            <option key={categoria.id} value={categoria.id}>
                              {'\u00A0'.repeat(livello * 2)}
                              {categoria.nome}
                            </option>
                          ))}
                      </select>
                    </td>
                    <td className="text-xs text-inchiostro-500">
                      {p.numeroInCausale && (
                        <span className="mr-1">
                          <Etichetta testo="numero citato" tono="info" />
                        </span>
                      )}
                      {p.motivo}
                    </td>
                    <td className="text-right">
                      <button
                        className="bottone-primario px-2 py-1 text-xs"
                        disabled={confermando !== null}
                        onClick={() => confermaProposta(p)}
                      >
                        Conferma
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Scheda>
      )}

      {(giroconti.dati ?? []).length > 0 && (
        <Scheda
          titolo="Possibili giroconti"
          sottotitolo="Stesso importo, segno opposto, due conti tuoi, date vicine. Mastro li propone soltanto: la decisione è tua."
          senzaPadding
        >
          <table className="dati">
            <thead>
              <tr>
                <th>Uscita</th>
                <th>Entrata</th>
                <th className="text-right">Importo</th>
                <th className="w-40" />
              </tr>
            </thead>
            <tbody>
              {giroconti.dati!.map((g) => (
                <tr key={`${g.uscita_id}-${g.entrata_id}`}>
                  <td className="text-xs">
                    <span className="num">{formattaData(g.data_uscita, organizzazione.formatoData)}</span> · {g.conto_uscita}
                    <span className="block max-w-72 truncate text-inchiostro-500">{g.dicitura_uscita}</span>
                  </td>
                  <td className="text-xs">
                    <span className="num">{formattaData(g.data_entrata, organizzazione.formatoData)}</span> · {g.conto_entrata}
                    <span className="block max-w-72 truncate text-inchiostro-500">{g.dicitura_entrata}</span>
                  </td>
                  <td className="text-right"><Importo cent={g.importo_cent} /></td>
                  <td>
                    <button
                      className="bottone-secondario w-full"
                      onClick={() => escludi([g.uscita_id, g.entrata_id], true)}
                      title="Un giroconto passato come movimento normale gonfia insieme incassi e uscite dello stesso importo."
                    >
                      Escludi la coppia
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scheda>
      )}

      <Scheda senzaPadding>
        <div className="flex flex-wrap items-end gap-3 border-b border-inchiostro-200 p-3 text-sm">
          <Campo etichetta="Cerca nella causale" larghezza="w-72">
            <input
              className="campo"
              value={cerca}
              onChange={(e) => imposta({ cerca: e.target.value, pagina: '' })}
              placeholder="Es. MEDIATEL"
            />
          </Campo>
          <Campo etichetta="Dal" larghezza="w-40">
            <input
              type="date"
              className="campo"
              value={da}
              onChange={(e) => imposta({ da: e.target.value, pagina: '' })}
            />
          </Campo>
          <Campo etichetta="Al" larghezza="w-40">
            <input
              type="date"
              className="campo"
              value={a}
              onChange={(e) => imposta({ a: e.target.value, pagina: '' })}
            />
          </Campo>
          <label className="flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={soloDaFare}
              onChange={(e) => imposta({ tutti: e.target.checked ? '' : 'si', pagina: '' })}
            />
            Solo movimenti da riconciliare
          </label>
          {(cerca || da || a) && (
            <button className="bottone-secondario" onClick={azzera}>
              Azzera i filtri
            </button>
          )}
          <span className="pb-2 text-inchiostro-500">
            {plurale(movimenti.dati?.totale ?? elenco.length, 'movimento', 'movimenti')}
          </span>
        </div>

        {movimenti.caricamento ? (
          <Caricamento />
        ) : movimenti.errore ? (
          <div className="p-4"><Errore testo={movimenti.errore} riprova={movimenti.ricarica} /></div>
        ) : elenco.length === 0 ? (
          <Vuoto
            titolo="Riconciliazione completa"
            testo="Tutti i movimenti risultano associati a un documento oppure convalidati."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="dati">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th>Causale</th>
                  <th>Voce</th>
                  <th className="text-right">Importo</th>
                  <th className="text-right">Da abbinare</th>
                  <th className="w-56" />
                </tr>
              </thead>
              <tbody>
                {elenco.map((m) => (
                  <tr key={m.id} className={m.escluso ? 'opacity-60' : ''}>
                    <td className="num whitespace-nowrap">{formattaData(m.data, organizzazione.formatoData)}</td>
                    <td className="whitespace-nowrap text-xs">{m.conto.nome}</td>
                    <td className="max-w-80 truncate" title={m.dicitura}>
                      {m.dicitura}
                      {m.senzaDocumentoIl && (
                        <span className="ml-2">
                          <Etichetta
                            testo="convalidato"
                            tono="ok"
                            titolo="Riconciliazione conclusa: il movimento non ha un documento da associare."
                          />
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => setCategorizza(m)}
                        className="text-left underline decoration-dotted underline-offset-2"
                      >
                        {m.categoria ? m.categoria.nome : <Etichetta testo="da categorizzare" tono="attenzione" />}
                      </button>
                      {/* Ogni proposta dichiara da quale regola arriva: una
                          proposta di cui non si sa la causa non si può né
                          valutare né correggere alla radice. */}
                      {m.regola && (
                        <span className="mt-0.5 block">
                          <Etichetta
                            testo={`da regola «${m.regola.dicitura}»`}
                            tono="info"
                            titolo="Proposta automatica: scegliendo a mano la sostituisci, e da lì in poi nessuna regola la tocca più."
                          />
                        </span>
                      )}
                    </td>
                    <td className="text-right"><Importo cent={m.importoCent} tono="auto" /></td>
                    <td className="text-right"><Importo cent={m.residuoCent} /></td>
                    <td>
                      <div className="flex justify-end gap-1.5">
                        <AzioniMovimento
                          convalidato={Boolean(m.senzaDocumentoIl)}
                          uscita={m.importoCent < 0}
                          onConvalida={() => convalida(m, !m.senzaDocumentoIl)}
                          onAssocia={() => setScelto(m)}
                          onAssociaF24={() => setAssociaF24(m)}
                        />
                        <button
                          className="bottone-secondario px-2 py-1 text-xs"
                          onClick={() => escludi([m.id], !m.escluso)}
                          title="Escludere non è cancellare: il movimento resta visibile e smette di pesare."
                        >
                          {m.escluso ? 'Reincludi' : 'Escludi'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {movimenti.dati && (
          <Paginazione
            scarto={movimenti.dati.scarto}
            mostrati={elenco.length}
            totale={movimenti.dati.totale}
            limite={movimenti.dati.limite}
            onPagina={setScarto}
          />
        )}
      </Scheda>

      {/* Le regole hanno una pagina loro, ma il legame va detto: molte delle
          voci proposte qui sopra vengono da lì, e chi vede una proposta
          sbagliata deve sapere dove si corregge. */}
      <p className="text-sm text-inchiostro-500">
        {regole.dati && regole.dati.length > 0 ? (
          <>
            {plurale(regole.dati.length, 'regola automatica assegna', 'regole automatiche assegnano')} la voce ai
            movimenti ricorrenti senza che tu la scelga.{' '}
          </>
        ) : (
          <>Nessuna regola automatica: le voci le stai assegnando tutte a mano. </>
        )}
        <Link to="/regole" className="underline underline-offset-2">
          Vedi le regole
        </Link>
      </p>

      {scelto && (
        <DialogoAbbina
          movimento={scelto}
          onChiudi={() => setScelto(null)}
          onFatto={() => {
            setScelto(null);
            movimenti.ricarica();
          }}
        />
      )}
      {associaF24 && (
        <DialogoAssociaF24
          movimento={associaF24}
          onChiudi={() => setAssociaF24(null)}
          onFatto={() => {
            setAssociaF24(null);
            movimenti.ricarica();
            proposte.ricarica();
          }}
        />
      )}
      {categorizza && (
        <DialogoCategoria
          movimento={categorizza}
          categorie={categorie.dati ?? []}
          onChiudi={() => {
            setCategorizza(null);
            setConvalidaDopoVoce(null);
          }}
          onFatto={async () => {
            const m = categorizza;
            const daConvalidare = convalidaDopoVoce === m.id;
            setCategorizza(null);
            setConvalidaDopoVoce(null);
            // L'operazione richiesta era la convalida e la voce ne era il
            // presupposto: si porta a termine, invece di interrompersi a metà
            // lasciando dubbio se abbia avuto effetto.
            if (daConvalidare) await registraConvalida(m.id, true);
            movimenti.ricarica();
            regole.ricarica();
          }}
        />
      )}
    </div>
  );
}

/**
 * I due esiti possibili di un movimento, presentati insieme.
 *
 * Sono due modi di concludere la stessa lavorazione — la convalida e
 * l'associazione a una fattura — e finché il secondo era l'unico pulsante il
 * primo sembrava non esistere: davanti a una commissione bancaria non c'era
 * nulla da premere, e il movimento restava in sospeso.
 */
interface PropostaDalGestionale {
  movimentoId: string;
  documenti: { documentoId: string; numero: string; importoCent: number }[];
  totaleCent: number;
  pagatoIl: string;
  giorniDiScarto: number;
  controparte: string | null;
  motivo: string;
  movimento: { data: string; importoCent: number; dicitura: string };
}

/**
 * Le fatture che Fatture in Cloud dà per pagate, con il movimento che le salda.
 *
 * Il gestionale sa già che sono pagate e quando: Mastro lo impara solo quando
 * il movimento è abbinato, e fino ad allora le tiene aperte nell'arretrato. Il
 * caso tipico è il bonifico cumulativo — sei fatture, un movimento solo che fa
 * la somma — che nessuna proposta per importo singolo avrebbe mai trovato.
 *
 * Una riga per movimento, con tutte le fatture che salda: è così che è
 * successo, ed è così che lo si controlla.
 */
function PagamentiRitrovati({
  percorso,
  titolo,
  sottotitolo,
  spiegazioneConferma,
  onConfermate,
}: {
  percorso: string;
  titolo: string;
  sottotitolo: string;
  spiegazioneConferma: string;
  onConfermate: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [inCorso, setInCorso] = useState(false);
  const dati = useCarico(
    () =>
      api.get<{ proposte: PropostaDalGestionale[]; totale: number; documenti: number; importoCent: number }>(
        percorso,
      ),
    [percorso],
  );
  const elenco = dati.dati?.proposte ?? [];
  if (elenco.length === 0) return null;

  const confermaTutte = async () => {
    if (
      !confirm(
        `Abbinare ${plurale(dati.dati!.documenti, 'fattura', 'fatture')} a ${plurale(
          elenco.length,
          'movimento',
          'movimenti',
        )}?\n\n` +
          `${spiegazioneConferma} Ognuna si può disabbinare dalla pagina della fattura.`,
      )
    )
      return;
    setInCorso(true);
    try {
      const r = await api.post<{ confermate: number; fatture: number; saltate: number }>(
        `${percorso}/conferma`,
        { attese: elenco.length },
      );
      avvisa(
        `${plurale(r.fatture, 'fattura abbinata', 'fatture abbinate')} a ${plurale(
          r.confermate,
          'movimento',
          'movimenti',
        )}` + (r.saltate ? `; ${r.saltate} saltate perché nel frattempo non reggevano più.` : '.'),
      );
      dati.ricarica();
      onConfermate();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Conferma non riuscita.', 'errore');
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Scheda
      titolo={titolo}
      sottotitolo={sottotitolo}
      senzaPadding
      azioni={
        <div className="flex items-center gap-3">
          <span className="text-sm text-inchiostro-500">
            {plurale(dati.dati!.documenti, 'fattura', 'fatture')} ·{' '}
            <span className="num">{formattaEuro(dati.dati!.importoCent)}</span>
          </span>
          <button className="bottone-primario" disabled={inCorso} onClick={() => void confermaTutte()}>
            {inCorso ? 'Abbino…' : 'Conferma tutte'}
          </button>
        </div>
      }
    >
      <div className="max-h-[28rem] overflow-y-auto">
        <table className="dati">
          <thead>
            <tr>
              <th>Movimento</th>
              <th>Causale</th>
              <th className="text-right">Importo</th>
              <th>Controparte</th>
              <th>Fatture</th>
              <th>Perché</th>
            </tr>
          </thead>
          <tbody>
            {elenco.map((p) => (
              <tr key={p.movimentoId}>
                <td className="num whitespace-nowrap">{formattaData(p.movimento.data, organizzazione.formatoData)}</td>
                <td className="max-w-72 truncate" title={p.movimento.dicitura}>
                  {p.movimento.dicitura}
                </td>
                <td className="text-right">
                  <Importo cent={p.movimento.importoCent} tono="auto" />
                </td>
                <td className="max-w-48 truncate">{p.controparte ?? '—'}</td>
                <td>
                  <span className="flex flex-wrap gap-x-2">
                    {p.documenti.map((d) => (
                      <LinkFattura key={d.documentoId} id={d.documentoId}>
                        {d.numero}
                      </LinkFattura>
                    ))}
                  </span>
                </td>
                <td className="text-xs text-inchiostro-500">{p.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Scheda>
  );
}

function AzioniMovimento({
  convalidato,
  uscita,
  onConvalida,
  onAssocia,
  onAssociaF24,
}: {
  convalidato: boolean;
  /** Un F24 è un versamento: su un accredito la voce non avrebbe senso. */
  uscita: boolean;
  onConvalida: () => void;
  onAssocia: () => void;
  onAssociaF24: () => void;
}) {
  const [aperto, setAperto] = useState(false);
  const contenitore = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) setAperto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAperto(false);
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperto]);

  const scegli = (azione: () => void) => {
    setAperto(false);
    azione();
  };

  return (
    <div className="relative" ref={contenitore}>
      <button
        className="bottone-secondario flex items-center gap-1 px-2 py-1 text-xs"
        aria-haspopup="menu"
        aria-expanded={aperto}
        onClick={() => setAperto((v) => !v)}
      >
        Riconcilia
        <span aria-hidden className="text-[0.6rem] leading-none">▾</span>
      </button>
      {aperto && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-inchiostro-200 bg-white py-1 shadow-lg"
        >
          <button
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-xs hover:bg-inchiostro-50"
            onClick={() => scegli(onConvalida)}
          >
            <span className="font-medium">{convalidato ? 'Annulla la convalida' : 'Convalida'}</span>
            <span className="mt-0.5 block text-inchiostro-500">
              {convalidato
                ? 'Il movimento torna fra quelli da riconciliare.'
                : 'Riconciliazione conclusa: il movimento non ha un documento da associare.'}
            </span>
          </button>
          <button
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-xs hover:bg-inchiostro-50"
            onClick={() => scegli(onAssocia)}
          >
            <span className="font-medium">Associa fattura</span>
            <span className="mt-0.5 block text-inchiostro-500">Collega il movimento a una o più fatture.</span>
          </button>
          {uscita && (
            <button
              role="menuitem"
              className="block w-full px-3 py-2 text-left text-xs hover:bg-inchiostro-50"
              onClick={() => scegli(onAssociaF24)}
            >
              <span className="font-medium">Associa F24</span>
              <span className="mt-0.5 block text-inchiostro-500">
                Questo addebito sta versando un modulo registrato.
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DialogoAbbina({
  movimento,
  onChiudi,
  onFatto,
}: {
  movimento: Movimento;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const [cerca, setCerca] = useState('');
  const [da, setDa] = useState('');
  const [a, setA] = useState('');
  const cercaRitardata = useDebounce(cerca);

  const query = new URLSearchParams({ limite: '60' });
  if (cercaRitardata) query.set('cerca', cercaRitardata);
  if (da) query.set('da', da);
  if (a) query.set('a', a);

  const { dati, caricamento } = useCarico(
    () =>
      api.get<{ movimento: Movimento; candidati: Candidato[]; totale: number }>(
        `/api/riconciliazione/candidati/${movimento.id}?${query}`,
      ),
    [movimento.id, cercaRitardata, da, a],
  );

  /**
   * Le fatture scelte, **nell'ordine in cui sono state spuntate**. L'ordine non
   * è un dettaglio: quando il movimento non basta a coprirle tutte, l'ultima
   * prende solo ciò che resta, e la stessa sequenza viene mandata al server —
   * così la ripartizione che si vede qui è esattamente quella che verrà scritta.
   *
   * Restano in questa lista anche quando una ricerca le fa sparire dalla
   * tabella: si cerca «Rossi», si spunta, si cerca «Bianchi», si spunta ancora,
   * e alla fine si abbinano tutte e due. Tenerle solo fra le righe visibili
   * significherebbe perderle a ogni parola digitata.
   */
  const [scelti, setScelti] = useState<Candidato[]>([]);
  const [inCorso, setInCorso] = useState(false);

  const spunta = (c: Candidato) =>
    setScelti((v) => (v.some((x) => x.id === c.id) ? v.filter((x) => x.id !== c.id) : [...v, c]));

  // La ripartizione **non è ricalcolata qui**: è la stessa funzione che usa il
  // server. Due implementazioni parallele dello stesso conto divergono, e a
  // divergere sarebbe proprio il numero guardato prima di premere.
  const ripartite = ripartisciSuDocumenti(movimento.residuoCent, scelti.map((c) => c.residuoCent));
  const quote = new Map(scelti.map((c, i) => [c.id, ripartite[i]]));
  const sommaFatture = scelti.reduce((t, c) => t + c.residuoCent, 0);
  const copertoCent = ripartite.reduce((t, q) => t + q, 0);
  const differenzaCent = sommaFatture - movimento.residuoCent;
  /**
   * Le fatture che non riceverebbero niente perché il movimento è già esaurito.
   * Non si mandano al server: una quota da zero non è un abbinamento, e il
   * server la rifiuterebbe facendo fallire **tutte** le altre insieme a lei.
   */
  const daAbbinare = scelti.filter((c) => (quote.get(c.id) ?? 0) > 0);
  const aSecco = scelti.length - daAbbinare.length;

  const abbinaScelti = async () => {
    setInCorso(true);
    try {
      const r = await api.post<{ copertoCent: number; residuoCent: number }>(
        '/api/riconciliazione/abbina-multiplo',
        { movimentoId: movimento.id, documentiId: daAbbinare.map((c) => c.id) },
      );
      avvisa(
        `${plurale(daAbbinare.length, 'fattura associata', 'fatture associate')} per ${formattaEuro(r.copertoCent)}` +
          (r.residuoCent > 0 ? `. Residuo del movimento non associato: ${formattaEuro(r.residuoCent)}.` : '.'),
      );
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Associazione non riuscita.', 'errore');
    } finally {
      setInCorso(false);
    }
  };

  const candidati = dati?.candidati ?? [];
  const filtrato = Boolean(cercaRitardata || da || a);

  return (
    <Dialogo aperto titolo="Associa il movimento alle fatture" onChiudi={onChiudi} larghezza="max-w-4xl">
      <div className="mb-3 rounded-md bg-inchiostro-50 p-3 text-sm">
        <p className="font-medium">{movimento.dicitura}</p>
        <p className="mt-0.5 text-inchiostro-600">
          {formattaData(movimento.data, organizzazione.formatoData)} · {movimento.conto.nome} ·{' '}
          <span className="num">{formattaEuro(movimento.importoCent)}</span>, da associare{' '}
          <span className="num font-medium">{formattaEuro(movimento.residuoCent)}</span>
        </p>
      </div>

      {/* Venti documenti somiglianti bastano quando il bonifico salda una
          fattura sola. Quando ne chiude tre di controparti diverse, la fattura
          giusta è quasi sempre fuori da quei venti. */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <Campo etichetta="Cerca" larghezza="w-64">
          <input
            className="campo"
            placeholder="Ragione sociale o numero"
            value={cerca}
            onChange={(e) => setCerca(e.target.value)}
          />
        </Campo>
        <Campo etichetta="Cassa attesa dal" larghezza="w-40">
          <input type="date" className="campo" value={da} onChange={(e) => setDa(e.target.value)} />
        </Campo>
        <Campo etichetta="al" larghezza="w-40">
          <input type="date" className="campo" value={a} onChange={(e) => setA(e.target.value)} />
        </Campo>
        {filtrato && (
          <button
            className="bottone-secondario mb-2"
            onClick={() => {
              setCerca('');
              setDa('');
              setA('');
            }}
          >
            Azzera
          </button>
        )}
      </div>

      {/* Il bordo non è decorazione: senza, la riga tagliata a metà dallo scorrimento
          sembra finire sotto il testo che viene dopo. */}
      <div className="max-h-[38vh] overflow-y-auto border-b border-inchiostro-200">
        {caricamento ? (
          <Caricamento />
        ) : candidati.length === 0 ? (
          <Vuoto
            titolo={filtrato ? 'Nessun documento corrisponde ai filtri' : 'Nessun documento scoperto del verso corrispondente'}
            testo={
              filtrato
                ? 'La ricerca considera ragione sociale e numero del documento. Riduci i criteri o amplia l’intervallo di date.'
                : movimento.importoCent > 0
                  ? 'Per un accredito servono fatture attive ancora da incassare.'
                  : 'Per un addebito servono fatture passive ancora da pagare.'
            }
          />
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th className="w-10" />
                <th>Documento</th>
                <th>Controparte</th>
                <th>Cassa attesa</th>
                <th className="text-right">Residuo</th>
                <th className="text-right">Quota</th>
              </tr>
            </thead>
            <tbody>
              {candidati.map((c) => {
                const preso = scelti.some((x) => x.id === c.id);
                const esatto = c.residuoCent === movimento.residuoCent;
                return (
                  <tr key={c.id} className={preso ? 'bg-inchiostro-50' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Associa la fattura ${c.numero}`}
                        checked={preso}
                        onChange={() => spunta(c)}
                      />
                    </td>
                    <td className="font-medium">
                      {c.numero}
                      {esatto && (
                        <span className="ml-2">
                          <Etichetta testo="importo esatto" tono="ok" />
                        </span>
                      )}
                    </td>
                    <td className="max-w-48 truncate" title={c.controparte ?? undefined}>
                      {c.controparte ?? '—'}
                    </td>
                    <td className="num">{formattaData(c.dataCassaAttesa, organizzazione.formatoData)}</td>
                    <td className="text-right">
                      <Importo cent={c.residuoCent} />
                    </td>
                    <td className="num text-right">
                      {!preso ? (
                        <span className="text-inchiostro-300">—</span>
                      ) : (quote.get(c.id) ?? 0) > 0 ? (
                        formattaEuro(quote.get(c.id) ?? 0)
                      ) : (
                        // «0,00 €» si legge come un errore di calcolo. Il dato
                        // reale è un altro: il movimento si è esaurito prima.
                        <span
                          className="text-attenzione"
                          title="Il movimento risulta già assegnato per intero alle fatture precedenti."
                        >
                          nessuna quota
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {dati && dati.totale > candidati.length && (
        <p className="mt-2 text-xs text-inchiostro-500">
          {dati.totale} documenti scoperti: sono elencati i {candidati.length} più affini per importo e data. Usa la
          ricerca per raggiungere gli altri.
        </p>
      )}

      {/* Il riepilogo della copertura, aggiornato a ogni selezione: senza, chi
          sceglie tre fatture da mille contro un bonifico da tremila dovrebbe
          affidarsi a una somma fatta a mente.

          Ancorato in fondo perché il dialogo scorre: con venti candidati il
          pulsante di conferma finiva oltre il bordo visibile. */}
      <div className="sticky bottom-0 -mx-5 -mb-5 mt-4 border-t border-inchiostro-200 bg-white px-5 py-3">
        {scelti.length === 0 ? (
          <p className="text-sm text-inchiostro-500">
            Seleziona le fatture che questo movimento sta pagando. Puoi sceglierne più d’una, anche in ricerche
            successive.
          </p>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              <span className="font-medium">
                {plurale(scelti.length, 'fattura selezionata', 'fatture selezionate')}
              </span>{' '}
              per <span className="num">{formattaEuro(sommaFatture)}</span>, movimento{' '}
              <span className="num">{formattaEuro(movimento.residuoCent)}</span>.{' '}
              {differenzaCent === 0 ? (
                <span className="font-medium text-incassi">Copertura esatta.</span>
              ) : differenzaCent < 0 ? (
                <span className="text-attenzione">
                  Residuo del movimento non associato:{' '}
                  <span className="num">{formattaEuro(-differenzaCent)}</span>.
                </span>
              ) : aSecco > 0 ? (
                <span className="text-attenzione">
                  Il movimento si esaurisce sulle prime {daAbbinare.length}:{' '}
                  {plurale(aSecco, 'la restante non riceve quota', 'le restanti non ricevono quota')}.
                </span>
              ) : (
                <span className="text-attenzione">
                  Copertura parziale dell’ultima fattura: scoperti{' '}
                  <span className="num">{formattaEuro(differenzaCent)}</span>.
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <button className="bottone-secondario" onClick={() => setScelti([])}>
                Annulla selezione
              </button>
              <button
                className="bottone-primario"
                onClick={abbinaScelti}
                disabled={inCorso || daAbbinare.length === 0}
              >
                {inCorso
                  ? 'Associazione in corso…'
                  : `Associa ${plurale(daAbbinare.length, 'fattura', 'fatture')} · ${formattaEuro(copertoCent)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialogo>
  );
}

/**
 * L'addebito che sta versando un F24 registrato.
 *
 * Il gesto esiste già dalla parte opposta — dalla pagina F24, «Segna versato» —
 * ma chi sta scorrendo l'estratto conto ha in mano il movimento, non il modulo,
 * e costringerlo ad andare a cercarlo altrove significa che l'estratto conto
 * resta lì da riconciliare.
 *
 * Collegare fa due cose in un colpo: il modulo passa da previsione a fatto, e il
 * movimento esce da questa lista. Non è un'uscita in più — quei soldi si vedono
 * già in banca: è la previsione che si fa da parte per non contarli due volte.
 */
function DialogoAssociaF24({
  movimento,
  onChiudi,
  onFatto,
}: {
  movimento: Movimento;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { organizzazione } = useSessione();
  const avvisa = useAvvisi();
  const { dati, caricamento } = useCarico(
    () => api.get<F24Aperto[]>('/api/f24?stato=DA_VERSARE'),
    [],
  );
  const [scelto, setScelto] = useState('');
  const [attesa, setAttesa] = useState(false);

  const aperti = dati ?? [];
  const importo = Math.abs(movimento.importoCent);
  // Prima quelli che somigliano di più: importo vicino, poi scadenza vicina.
  const ordinati = [...aperti].sort(
    (a, b) =>
      Math.abs(a.saldoCent - importo) - Math.abs(b.saldoCent - importo) ||
      a.dataScadenza.localeCompare(b.dataScadenza),
  );

  const collega = async () => {
    setAttesa(true);
    try {
      // La data del versamento è quella del movimento: è la banca a dire quando
      // i soldi sono usciti, non la scadenza del modulo.
      await api.post(`/api/f24/${scelto}/versa`, {
        dataVersamento: movimento.data,
        movimentoId: movimento.id,
      });
      avvisa('F24 collegato: risulta versato e il movimento esce dalla riconciliazione.');
      onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Collegamento non riuscito.', 'errore');
    } finally {
      setAttesa(false);
    }
  };

  return (
    <Dialogo aperto titolo="Associa il movimento a un F24" onChiudi={onChiudi} larghezza="max-w-3xl">
      <div className="mb-3 rounded-md bg-inchiostro-50 p-3 text-sm">
        <p className="font-medium">{movimento.dicitura}</p>
        <p className="mt-0.5 text-inchiostro-600">
          {formattaData(movimento.data, organizzazione.formatoData)} · {movimento.conto.nome} ·{' '}
          <span className="num font-medium">{formattaEuro(movimento.importoCent)}</span>
        </p>
      </div>

      {caricamento ? (
        <Caricamento />
      ) : ordinati.length === 0 ? (
        <Vuoto
          titolo="Nessun F24 da versare"
          testo="Registra prima il modulo con la sua scadenza: solo così l’uscita compare nella previsione di cassa prima di avvenire."
        />
      ) : (
        <div className="max-h-[45vh] overflow-y-auto border-y border-inchiostro-200">
          <table className="dati">
            <thead>
              <tr>
                <th className="w-10" />
                <th>Scadenza</th>
                <th>Descrizione</th>
                <th className="text-right">Da versare</th>
              </tr>
            </thead>
            <tbody>
              {ordinati.map((f) => (
                <tr key={f.id} className={scelto === f.id ? 'bg-inchiostro-50' : ''}>
                  <td>
                    <input
                      type="radio"
                      name="f24"
                      aria-label={`Associa ${f.descrizione}`}
                      checked={scelto === f.id}
                      onChange={() => setScelto(f.id)}
                    />
                  </td>
                  <td className="num whitespace-nowrap">{formattaData(f.dataScadenza, organizzazione.formatoData)}</td>
                  <td className="font-medium">
                    {f.descrizione}
                    {f.saldoCent === importo && (
                      <span className="ml-2">
                        <Etichetta testo="importo esatto" tono="ok" />
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    <Importo cent={f.saldoCent} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-sm text-inchiostro-500">
        Non trovi il modulo?{' '}
        <Link to="/f24" className="underline underline-offset-2">
          Registralo
        </Link>{' '}
        — oppure, se dietro questo addebito non c’è nessun modulo da tenere, convalidalo e basta.
      </p>

      <div className="mt-4 flex justify-end gap-2">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={collega} disabled={!scelto || attesa}>
          {attesa ? 'Collego…' : 'Collega e segna versato'}
        </button>
      </div>
    </Dialogo>
  );
}

function DialogoCategoria({
  movimento,
  categorie,
  onChiudi,
  onFatto,
}: {
  movimento: Movimento;
  categorie: Categoria[];
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const avvisa = useAvvisi();
  const { piatto } = useAlberoCategorie(categorie);
  const verso = movimento.importoCent >= 0 ? 'ENTRATA' : 'USCITA';
  // Le categorie di sistema non si assegnano a mano: sono il posto dove va ciò
  // che non è stato deciso, e offrirle sarebbe un modo per non decidere.
  const assegnabili = piatto.filter((p) => p.categoria.verso === verso && !p.categoria.sistema);
  const [scelta, setScelta] = useState(movimento.categoriaId ?? '');

  // Dopo aver assegnato la voce si chiede se renderla automatica: è il momento
  // in cui una persona ha in mente *perché* quel movimento va lì, e chiederlo
  // dopo — in un'altra pagina, a freddo — vuol dire non chiederlo mai.
  const [propostaRegola, setPropostaRegola] = useState<{ dicitura: string; categoriaId: string } | null>(null);

  const salva = async () => {
    try {
      const r = await api.post<{ suggerimento?: { dicitura: string } }>('/api/movimenti/categorizza', {
        movimentiId: [movimento.id],
        categoriaId: scelta || null,
      });
      avvisa('Voce assegnata. Da ora nessuna regola automatica la sovrascrive.');
      const dicitura = r?.suggerimento?.dicitura;
      if (scelta && dicitura) setPropostaRegola({ dicitura, categoriaId: scelta });
      else onFatto();
    } catch (e) {
      avvisa(e instanceof Error ? e.message : 'Assegnazione non riuscita.', 'errore');
    }
  };

  if (propostaRegola) {
    return (
      <DialogoRegola
        categorie={categorie}
        dicituraIniziale={propostaRegola.dicitura}
        versoIniziale={verso}
        categoriaIniziale={propostaRegola.categoriaId}
        dataMovimento={movimento.data}
        titolo="Vuoi che sia automatico?"
        introduzione={`Hai messo questo movimento sotto una voce. Se la causale si ripete ogni mese, una regola può assegnarla da sola — e tu non la tocchi più.`}
        onChiudi={() => {
          setPropostaRegola(null);
          onFatto();
        }}
        onCreata={() => {
          setPropostaRegola(null);
          onFatto();
        }}
      />
    );
  }

  return (
    <Dialogo aperto titolo="Assegna la voce del previsionale" onChiudi={onChiudi}>
      <p className="mb-3 text-sm text-inchiostro-600">{movimento.dicitura}</p>
      {/* Il menu non ha un valore preselezionato: partire su una voce mentre si
          legge il nome di un'altra è una trappola silenziosa. */}
      <ul className="max-h-80 space-y-0.5 overflow-y-auto rounded-md border border-inchiostro-200 p-1">
        {assegnabili.map(({ categoria, livello }) => (
          <li key={categoria.id}>
            <button
              onClick={() => setScelta(categoria.id)}
              style={{ paddingLeft: `${0.5 + livello * 1.1}rem` }}
              className={`w-full rounded px-2 py-1.5 text-left text-sm ${
                scelta === categoria.id ? 'bg-inchiostro-900 text-white' : 'hover:bg-inchiostro-100'
              }`}
            >
              {categoria.nome}
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        <button className="bottone-secondario" onClick={onChiudi}>
          Annulla
        </button>
        <button className="bottone-primario" onClick={salva} disabled={!scelta}>
          Assegna
        </button>
      </div>
    </Dialogo>
  );
}

// ── Regole automatiche ──────────────────────────────────────────────────────
